import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { cities } from '../cities';
import { Search, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Wiederverwendbare Accordion-Komponente für Regionen/Städte
 * Kann sowohl auf der Homepage als auch auf der /regionen Seite verwendet werden
 */
const RegionalAccordion = ({ 
  theme = 'dark', // 'dark' oder 'light'
  showSearch = true,
  containerClassName = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStates, setExpandedStates] = useState([]);

  // Gruppiere Städte nach Bundesland
  const citiesByState = cities.reduce((acc, city) => {
    if (!acc[city.state]) {
      acc[city.state] = [];
    }
    acc[city.state].push(city);
    return acc;
  }, {});

  // Sortiere Bundesländer alphabetisch
  const sortedStates = Object.keys(citiesByState).sort();

  // Sortiere Städte innerhalb jedes Bundeslandes alphabetisch
  Object.keys(citiesByState).forEach(state => {
    citiesByState[state].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  });

  // Hilfsfunktion: Prüft, ob eine PLZ in einem PLZ-Bereich liegt
  const isZipCodeInRange = (zipCode, zipRange) => {
    if (!zipCode) return false;
    
    // Nimm nur die ersten 5 Ziffern der Sucheingabe
    const searchZip = parseInt(zipCode.substring(0, 5));
    
    // Wenn die Eingabe keine gültige Zahl ist, return false
    if (isNaN(searchZip)) return false;
    
    // Split by comma for multiple ranges/values
    const ranges = zipRange.split(',').map(r => r.trim());
    
    for (const range of ranges) {
      // Prüfe ob es ein Bereich ist (z.B. "10115-14199")
      if (range.includes('-')) {
        const [start, end] = range.split('-').map(z => parseInt(z));
        if (searchZip >= start && searchZip <= end) return true;
      } else {
        // Einzelne PLZ
        const rangePlz = parseInt(range);
        if (rangePlz === searchZip) return true;
      }
    }
    
    return false;
  };

  // Filtere Bundesländer basierend auf Suchbegriff (Postleitzahl, Region oder Bundesland)
  const filteredStates = sortedStates
    .map(state => ({
      state,
      cities: citiesByState[state].filter(city => {
        // Wenn Suchbegriff nur Zahlen enthält, behandle als PLZ
        if (searchTerm && /^\d+$/.test(searchTerm)) {
          // Ab 2 Ziffern: PLZ-Suche aktivieren
          if (searchTerm.length >= 2) {
            return isZipCodeInRange(searchTerm, city.zipCodes);
          }
          return false;
        }
        // Ansonsten normale Textsuche
        return city.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               city.state.toLowerCase().includes(searchTerm.toLowerCase());
      })
    }))
    .filter(item => item.cities.length > 0);

  const totalFilteredCities = filteredStates.reduce((sum, item) => sum + item.cities.length, 0);

  const toggleState = (state) => {
    setExpandedStates(prev => 
      prev.includes(state) 
        ? prev.filter(s => s !== state)
        : [...prev, state]
    );
  };

  // Theme-spezifische Styles
  const isDark = theme === 'dark';
  
  const styles = {
    container: isDark 
      ? 'bg-white rounded-lg shadow-lg overflow-hidden'
      : 'bg-white rounded-lg shadow-lg overflow-hidden',
    searchContainer: isDark
      ? 'bg-white rounded-lg shadow-lg p-6 mb-8'
      : 'bg-white rounded-lg shadow-lg p-6 mb-8',
    stateHeader: isDark
      ? 'w-full flex items-center justify-between p-6 bg-gradient-to-r from-[#1A3A2E] to-[#2D4A3E] hover:from-[#2D4A3E] hover:to-[#3A5A4E] transition-all text-white'
      : 'w-full flex items-center justify-between p-6 bg-gradient-to-r from-[#1A3A2E] to-[#2D4A3E] hover:from-[#2D4A3E] hover:to-[#3A5A4E] transition-all text-white',
    stateTitle: isDark
      ? 'text-2xl font-bold text-white'
      : 'text-2xl font-bold text-white',
    stateCount: isDark
      ? 'text-sm text-[#E8F5E0]/70 mt-1'
      : 'text-sm text-[#E8F5E0]/70 mt-1',
    citiesContainer: isDark
      ? 'p-6 bg-gradient-to-b from-[#E8F5E0]/20 to-white'
      : 'p-6 bg-gradient-to-b from-[#E8F5E0]/20 to-white',
    cityCard: isDark
      ? 'group p-4 border-2 border-[#1A3A2E]/10 rounded-lg hover:border-[#1A3A2E] hover:bg-[#E8F5E0] transition-all duration-300 bg-white'
      : 'group p-4 border-2 border-[#1A3A2E]/10 rounded-lg hover:border-[#1A3A2E] hover:bg-[#E8F5E0] transition-all duration-300 bg-white'
  };

  return (
    <div className={containerClassName}>
      {/* Suchfeld */}
      {showSearch && (
        <div className={styles.searchContainer}>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#1A3A2E]/50 w-5 h-5" />
            <input
              type="text"
              placeholder="Postleitzahl suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-[#1A3A2E]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3A2E] focus:border-transparent text-base"
            />
          </div>
        </div>
      )}

      {/* Bundesländer mit Dropdown-Menü */}
      <div className="space-y-4">
        {filteredStates.map(({ state, cities: stateCities }) => {
          const isExpanded = expandedStates.includes(state);
          
          return (
            <div key={state} className={`${styles.container} border-2 border-[#1A3A2E]/10`}>
              {/* Bundesland Header - Klickbar */}
              <button
                onClick={() => toggleState(state)}
                className={styles.stateHeader}
              >
                <div className="flex items-center gap-4">
                  <MapPin className="w-6 h-6 text-[#E8F5E0]" />
                  <div className="text-left">
                    <h2 className={styles.stateTitle}>{state}</h2>
                    <p className={styles.stateCount}>
                      {stateCities.length} {stateCities.length === 1 ? 'Region' : 'Regionen'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  {isExpanded ? (
                    <ChevronUp className="w-8 h-8 text-[#E8F5E0]" />
                  ) : (
                    <ChevronDown className="w-8 h-8 text-[#E8F5E0]" />
                  )}
                </div>
              </button>

              {/* Dropdown - Städte-Liste */}
              {isExpanded && (
                <div className={styles.citiesContainer}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stateCities.map(city => (
                      <Link
                        key={city.slug}
                        to={`/${city.slug}`}
                        className={styles.cityCard}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-[#1A3A2E] group-hover:text-[#2A4A3E] mb-1">
                              {city.name}
                            </h3>
                            <p className="text-sm text-[#1A3A2E]/70 mb-2">
                              {city.region}
                            </p>
                            <p className="text-xs text-[#1A3A2E]/50">
                              PLZ: {city.zipCodes}
                            </p>
                          </div>
                          <div className="text-[#1A3A2E] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                            →
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Keine Ergebnisse */}
      {filteredStates.length === 0 && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-2xl font-bold text-[#1A3A2E] mb-2">
            Keine Regionen gefunden
          </h3>
          <p className="text-[#1A3A2E]/70 mb-6">
            Versuchen Sie einen anderen Suchbegriff.
          </p>
          <button
            onClick={() => setSearchTerm('')}
            className="bg-[#1A3A2E] text-white px-6 py-3 rounded-lg hover:bg-[#2A4A3E] transition-colors font-semibold"
          >
            Suche zurücksetzen
          </button>
        </div>
      )}
    </div>
  );
};

export default RegionalAccordion;
